import { stripHtml, formatDateTime } from "@/shared/format";
import type { ThreadMessage } from "../api";
import { messageSide, senderLabel } from "../messageSide";
import { AttachmentList } from "./AttachmentList";
import { IconaMondo } from "./AzioniComposer";

export function MessageBubble({
  message,
  translatedText,
  translationVisible,
  isTranslating,
  onToggleTranslate,
}: {
  message: ThreadMessage;
  translatedText?: string;
  translationVisible: boolean;
  isTranslating: boolean;
  onToggleTranslate: () => void;
}) {
  // Bug 2026-06-11: i messaggi OPERATOR (customer-care del marketplace che
  // inoltra il cliente) comparivano a destra come i nostri → chat
  // illeggibile. Ora: destra = SOLO lato shop; operator a sinistra con
  // stile ed etichetta propri. Vedi messageSide.ts.
  const side = messageSide(message);
  const isOutgoing = side === "mine";
  const sourceText = message.body_plain || stripHtml(message.body_html);
  const hasTranslation = translatedText !== undefined;
  const showTranslation = hasTranslation && translationVisible;
  return (
    <div className={["flex", isOutgoing ? "justify-end" : "justify-start"].join(" ")}>
      <div
        className={[
          // Redesign 2026-10-27: testo sempre nero (era grigio sui messaggi
          // del cliente), angolo «a coda» verso chi scrive.
          "max-w-[680px] rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed text-ink max-md:max-w-[92%]",
          isOutgoing
            ? "rounded-br-md border border-accent/25 bg-accent-soft"
            : side === "operator"
              ? "rounded-bl-md border-[1.5px] border-dashed border-line-2 bg-paper-2"
              : "rounded-bl-md border border-line-2 bg-card shadow-[0_1px_2px_rgba(31,27,22,0.05)]",
        ].join(" ")}
      >
        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-[12.5px] font-bold">{senderLabel(message, side)}</span>
          <span className="text-[11.5px] tabular-nums text-ink">
            {formatDateTime(message.created_at_remote)}
          </span>
          <button
            type="button"
            onClick={onToggleTranslate}
            disabled={isTranslating || (!sourceText.trim() && !hasTranslation)}
            title={
              hasTranslation
                ? translationVisible
                  ? "Nascondi traduzione"
                  : "Mostra traduzione"
                : "Traduci in italiano"
            }
            aria-label={
              hasTranslation
                ? translationVisible
                  ? "Nascondi traduzione"
                  : "Mostra traduzione"
                : "Traduci in italiano"
            }
            className="ml-auto inline-flex h-7 items-center gap-1 rounded-full border border-line-2 bg-white/70 px-2.5 text-[11.5px] font-semibold text-ink transition-colors hover:border-ink disabled:opacity-40"
          >
            <IconaMondo />
            {isTranslating
              ? "Traduco…"
              : hasTranslation
                ? translationVisible
                  ? "Nascondi"
                  : "Italiano"
                : "Traduci"}
          </button>
        </div>
        <div className="whitespace-pre-wrap break-words">{sourceText}</div>
        {showTranslation && (
          <div className="mt-2.5 rounded-xl bg-white/70 px-3 py-2 ring-1 ring-line-2">
            <div className="mb-0.5 text-[11.5px] font-bold text-ink">In italiano</div>
            <div className="whitespace-pre-wrap break-words text-[13px] text-ink">
              {translatedText}
            </div>
          </div>
        )}
        <AttachmentList attachments={message.attachments} />
        {/* Audit 2026-05-28 (Davide): "preview foto in ricezione +
            scarica in Downloads". AttachmentList renderizza preview
            thumbnail per ``image/*`` (click → ImageLightbox fullscreen)
            e chip con bottone scarica per gli altri MIME. */}
      </div>
    </div>
  );
}
